import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import { catchError, map, retry } from "rxjs/operators";
import { ConfirmationSteps, Confirmations, Delivery, WebserviceResponse } from "../interfaces";
import { normalizeSingletonResponse } from "../utils/response";
import { restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class ConfirmationStoreService {

  private readonly endpoint = restBaseUrl('/confirmation');

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService) {
  }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  public createConfirmation(projectID: string, jobID: number): Observable<WebserviceResponse<boolean>> {
    return this.http.post<WebserviceResponse<boolean>>(
        `${this.endpoint}/add_confirmation?project_id=${projectID}&job_id=${jobID}`,
        {responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteConfirmation(projectID: string, confirmationID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_confirmation?project_id=${projectID}&confirmation_id=${confirmationID}`,
        {responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateConfirmation(c: ConfirmationSteps, jobID: number) {
    return  this.http.put(
        `${this.endpoint}/update_confirmation?job_id=${jobID}`,
        c,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getConfirmation(projectID: string, jobID: number, confirmationID: number): Observable<WebserviceResponse<Confirmations[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectID,
        confirmation_id: confirmationID,
        job_id: jobID
      }
    });
    return this.http.get<WebserviceResponse<Confirmations[]>>(this.endpoint + '/get_confirmations',
        {params: options}).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getSingleConfirmation(projectID: string, confirmationID: string): Observable<WebserviceResponse<Confirmations>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectID,
        confirmation_id: confirmationID
      }
    });
    return this.http.get<WebserviceResponse<Confirmations>>(this.endpoint + '/get_single_confirmation',
        {params: options}).pipe(
        map((response) => normalizeSingletonResponse(response)),
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getConfirmationsPosition(projectID: string, confirmationID: number, positionID: string, partID: string): Observable<WebserviceResponse<Delivery[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectID,
        confirmation_id: confirmationID,
        position: positionID,
        part: partID
      }
    });
    return this.http.get<WebserviceResponse<Delivery[]>>(this.endpoint + '/get_confirmation_position',
        { params: options }).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateConfirmationsPosition(confirmation: ConfirmationSteps) {
    return this.http.put(
        `${this.endpoint}/update_confirmation_position`,
        confirmation,
        { responseType: 'json'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteConfirmationsPosition(projectID: string, confID: number, positionID: number, partID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_confirmation_position?project_id=${projectID}&confirmation_id=${confID}&position=${positionID}&part=${partID}`,
        { responseType: 'text'}
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }
}
